import { Stack } from "@mui/material";
import type { FC } from "react";
import type { ClientPropertyCottageDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { PropertyValueRows } from "web_myplann/Portfolio/Components/Redesign/Detail/PropertyValueRows";
import { BUILDING_OPTIONAL_PARAMS } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/buildingOptionalParams";
import { PropertyExtendedParams } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyExtendedParams";
import { PropertyPurchaseDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyPurchaseDetails";
import { getBuildingMaterial } from "web_myplann/Portfolio/Helpers/propertyBuildingAttributes";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyCottageDetailResponseDTO;
	currencySign: string;
};

export const CottageRedesign: FC<Props> = ({ data, currencySign }) => {
	const { t } = useResource();

	const { properties } = useGetPropertyOptionalParams(
		BUILDING_OPTIONAL_PARAMS,
		data,
		t,
	);

	const buildingMaterial = getBuildingMaterial(data, t);

	return (
		<>
			<Stack bgcolor="background.paper">
				<DetailRow
					label={t(PageResources.Property.PropertyTypeRow)}
					value={t(PageResources.Property.PropertyType[data.type])}
				/>
				<DetailRow
					label={t(PageResources.Property.UsableArea)}
					value={unitFormatter(data.usableArea, UNITS.squareMeter)}
				/>
				<PropertyValueRows
					reproductionValue={data.reproductionValue}
					interiorValue={data.interiorValue}
					currencySign={currencySign}
				/>
				<DetailRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<PropertyExtendedParams
					noun="realEstate"
					values={[
						unitFormatter(data.floorArea, UNITS.squareMeter),
						buildingMaterial,
						data.numberOfFloors,
						properties,
					]}
				>
					<DetailRow
						label={t(PageResources.Property.FloorArea)}
						value={unitFormatter(data.floorArea, UNITS.squareMeter)}
					/>
					<DetailRow
						label={t(PageResources.Property.BuildingMaterial)}
						value={buildingMaterial}
					/>
					<DetailRow
						label={t(PageResources.Property.NumberOfAboveGroundFloors)}
						value={data.numberOfFloors}
					/>
					<DetailRow
						label={t(PageResources.Property.Properties)}
						value={properties}
					/>
				</PropertyExtendedParams>
			</Stack>

			<PropertyPurchaseDetails description={data.description} />
		</>
	);
};
